import classNames from "classnames";
import { Image } from "./Image";
import { useEffect, useRef, useState } from "react";

export const ImageSlider = ({
  images,
}: {
  images: Array<Travelguide.Image>;
}) => {
  const [current, setCurrent] = useState<Array<string>>([]);
  const itemRefs = useRef(new Map<number, HTMLElement>()).current;
  const galleryRef = useRef<HTMLDivElement>(null);

  function moveTo(direction: -1 | 1) {
    return () => {
      current.sort();
      const currentIndex = parseInt(
        (direction < 0 ? current.slice(0, 1) : current.slice(-1)).shift() || ""
      );

      const newIndex = Math.min(
        itemRefs.size - 1,
        Math.max(0, currentIndex + (direction < 0 ? -3 : 1))
      );

      galleryRef.current?.scrollTo({
        left: itemRefs.get(newIndex)?.offsetLeft,
        behavior: "smooth",
      });
    };
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrent((prev) => [
              ...prev,
              (entry.target as HTMLElement).dataset.index || "",
            ]);
          } else {
            setCurrent((prev) =>
              prev.filter(
                (id) => id !== (entry.target as HTMLElement).dataset.index || ""
              )
            );
          }
        });
      },
      {
        root: galleryRef.current,
        threshold: 0.1,
      }
    );

    itemRefs.forEach((itemRef) => observer.observe(itemRef));

    return () => {
      itemRefs.forEach((itemRef) => observer.unobserve(itemRef));
    };
  }, [itemRefs]);

  return (
    <div className="relative h-min">
      <div
        ref={galleryRef}
        className={classNames(
          "grid grid-flow-col grid-cols-[1fr] auto-cols-min",
          "md:grid-cols-[repeat(3,minmax(32.65%,1fr))] md:auto-cols-[minmax(32.65%,1fr)] md:gap-[1%]",
          "overflow-x-scroll snap-x snap-mandatory"
        )}
      >
        {images.map((image, i) => (
          <div
            key={i}
            data-index={i}
            ref={(ref) => {
              if (ref) itemRefs.set(i, ref);
            }}
            className="snap-start aspect-1 h-full"
          >
            <Image
              bakeSettings={{ width: 800, height: 800 }}
              image={image}
              fill
              skeleton={true}
            />
          </div>
        ))}
      </div>
      <nav className="absolute w-full top-1/2 pointer-events-none">
        <label
          onClick={moveTo(-1)}
          className={classNames(
            "absolute left-2 cursor-pointer pointer-events-auto",
            "after:border-t-2 after:border-l-2 after:border-white after:block",
            "after:w-6 after:h-6 after:-rotate-45 after:translate-x-2",
            { hidden: current.includes("0") }
          )}
        ></label>
        <label
          onClick={moveTo(1)}
          className={classNames(
            "absolute right-2 cursor-pointer pointer-events-auto",
            "before:border-t-2 before:border-r-2 before:border-white before:block",
            "before:w-6 before:h-6 before:rotate-45 before:-translate-x-2",
            { hidden: current.includes(String(images.length - 1)) }
          )}
        ></label>
      </nav>
    </div>
  );
};
